package gr.tech.pdfgenerator.sections

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.layout.HorizontalDivider
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toAttrs
import gr.tech.pdfgenerator.util.StateAppendix
import gr.tech.pdfgenerator.infrastructure.data.GeneratorData
import gr.tech.pdfgenerator.util.formatCurrency
import gr.tech.pdfgenerator.styles.PropDerivativeLabelStyle
import gr.tech.pdfgenerator.styles.PropDerivativeSpanStyle
import gr.tech.pdfgenerator.util.CONSTANTS.FONT_FAMILY
import gr.tech.pdfgenerator.util.INPUTS
import gr.tech.pdfgenerator.infrastructure.calculations.recalculateDerivations
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.*

@Composable
fun proposalInputsSection(
    stateAppendix: StateAppendix,
) {
    var propInputState by GeneratorData.propInputState
    var derivationState by GeneratorData.derivationsState

    LaunchedEffect(propInputState) {
        derivationState = recalculateDerivations(propInputState)
    }

    Row(
        modifier = Modifier
            .width(1032.px)
            .gap(16.px)
            .padding(bottom = 12.px)
    ) {
        Column(
            modifier = Modifier
                .width(50.percent)
        ) {
            LongInputLabel(
                label = "Baseline kWh",
                id = INPUTS.PROJECT_PROPOSAL.propBaselinekWhInput,
                initialValue = propInputState.baselineKWH.toString()
            ) {
                propInputState = propInputState.copy(baselineKWH = it)
            }
            LongInputLabel(
                label = "Baseline Fixture Count",
                id = INPUTS.PROJECT_PROPOSAL.propBaselineFixtureCountInput,
                initialValue = propInputState.baselineFixCount.toString()
            ) {
                propInputState = propInputState.copy(baselineFixCount = it)
            }
            HorizontalDivider()
            LongInputLabel(
                label = "Proposed kWh",
                id = INPUTS.PROJECT_PROPOSAL.propProposedkWhInput,
                initialValue = propInputState.proposedKWH.toString()
            ) {
                propInputState = propInputState.copy(proposedKWH = it)
            }
            LongInputLabel(
                label = "Proposed Fixture Count",
                id = INPUTS.PROJECT_PROPOSAL.propProposedFixtureCountInput,
                initialValue = propInputState.proposedFixCount.toString()
            ) {
                propInputState = propInputState.copy(proposedFixCount = it)
            }
            HorizontalDivider(Modifier.color(Colors.Grey))
            DollarInputLabel(
                label = "Project Cost ($)",
                id = INPUTS.PROJECT_PROPOSAL.propProjectCostInput,
                initialValue = propInputState.projectCost.toString()
            ) {
                propInputState = propInputState.copy(projectCost = it)
            }
            DollarInputLabel(
                label = "Rebate Amount ($)",
                id = INPUTS.PROJECT_PROPOSAL.propRebateAmountInput,
                initialValue = propInputState.rebate.toString()
            ) {
                propInputState = propInputState.copy(rebate = it)
            }
            HorizontalDivider(Modifier.color(Colors.Grey))

            Row(
                Modifier
                    .gap(16.px)
                    .fillMaxWidth()
                    .fillMaxHeight(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.Bottom
            ) {
                if (stateAppendix.isDataLoaded.value) {
                    CustomDropdown(
                        itemList =  stateAppendix.listOfStates(),
                        id = INPUTS.PROJECT_PROPOSAL.propStateInput,
                        initialValue = propInputState.selectedState?.stateAbb
                    ) { state ->
                        val selectedState = stateAppendix.getStateInfo(state)
                        if (selectedState != null) {
                            propInputState = propInputState.copy(
                                selectedState = selectedState,
                                selectedStateElectricityRate = selectedState.rebateRate,
                            )
                        }
                    }
                }

                DoubleInputLabel(
                    label = "Overwrite Electricity Rate",
                    id = INPUTS.PROJECT_PROPOSAL.propElectricityRateInput,
                    initialValue = propInputState.selectedStateElectricityRate
                ) {
                    propInputState = propInputState.copy(selectedStateElectricityRate = it)
                }
            }

            Column(
                modifier = Modifier
                    .margin(top = 16.px)
                    .fillMaxWidth(),
                horizontalAlignment = Alignment.Start
            ) {
                ToggleButton(
                    label = "Add Finance Page?",
                    id = INPUTS.PROJECT_PROPOSAL.propAddFinancePageInput,
                    disabled = true, // Disabled due to not being needed right now.
                    initialValue = propInputState.toggleFinancePage
                ) {
                    propInputState = propInputState.copy(toggleFinancePage = !propInputState.toggleFinancePage)
                }

                ToggleButton(
                    label = "Add Leasing Page?",
                    id = INPUTS.PROJECT_PROPOSAL.propAddLeasingPageInput,
                    disabled = true, // Disabled due to not being needed right now.
                    initialValue = propInputState.toggleLeasingPage
                ) {
                    propInputState = propInputState.copy(toggleLeasingPage = !propInputState.toggleLeasingPage)
                }
            }
        }
        Column(
            modifier = Modifier
                .width(50.percent)
        ) {
            H3(
                attrs = Modifier
                    .fontFamily(FONT_FAMILY)
                    .margin(bottom = 12.px)
                    .fontSize(1.25.cssRem)
                    .toAttrs()
            ) {
                Text("Assumptions")
            }
            PercentInputLabel(
                label = "Utility Rate Inflation (%)",
                id = INPUTS.PROJECT_PROPOSAL.propUtilityRateInflationInput,
                initialValue = propInputState.utilityRateInflation
            ) {
                propInputState = propInputState.copy(utilityRateInflation = it)
            }

            PercentInputLabel(
                label = "Utility Rate Inflation (%)",
                id = INPUTS.PROJECT_PROPOSAL.propHVACInteractionSavingsInput,
                initialValue = propInputState.hvacInteractionSavings
            ) {
                propInputState = propInputState.copy(hvacInteractionSavings = it)
            }

            PercentInputLabel(
                label = "Utility Rate Inflation (%)",
                id = INPUTS.PROJECT_PROPOSAL.propHVACUpTimeInput,
                initialValue = propInputState.hvacUpTime
            ) {
                propInputState = propInputState.copy(hvacUpTime = it)
            }

            DollarInputLabel(
                label = "HPS Annual Maintenance ($)",
                id = INPUTS.PROJECT_PROPOSAL.propHPSMaintenanceInput,
                initialValue = propInputState.hpsMaintenance.toString()
            ) {
                propInputState = propInputState.copy(hpsMaintenance = it)
            }

            H3(
                attrs = Modifier
                    .fontFamily(FONT_FAMILY)
                    .fontSize(1.25.cssRem)
                    .margin(0.px)
                    .toAttrs()
            ) {
                Text("Derivations")
            }
            SpanText(
                text = "(Calculated From Inputs)",
                modifier = Modifier
                    .fontFamily(FONT_FAMILY)
                    .margin(bottom = 12.px)
            )

            P(PropDerivativeLabelStyle.toAttrs()) {
                Text("Electricity Rate: ")
                Span(PropDerivativeSpanStyle.toAttrs()) {
                    Text(derivationState.electricityRate.toString())
                }
            }

            P(PropDerivativeLabelStyle.toAttrs()) {
                Text("Annual kWh Saved: ")
                Span(PropDerivativeSpanStyle.toAttrs()) {
                    Text(derivationState.annualKWHSaved.toString())
                }
            }

            P(PropDerivativeLabelStyle.toAttrs()) {
                Text("Energy Savings (Lighting): ")
                Span(PropDerivativeSpanStyle.toAttrs()) {
                    Text(formatCurrency(derivationState.energySavingsLighting))
                }
            }

            P(PropDerivativeLabelStyle.toAttrs()) {
                Text("Energy Savings (HVAC): ")
                Span(PropDerivativeSpanStyle.toAttrs()) {
                    Text(formatCurrency(derivationState.energySavingsHVAC))
                }
            }

            P(PropDerivativeLabelStyle.toAttrs()) {
                Text("Maintenance Savings: ")
                Span(PropDerivativeSpanStyle.toAttrs()) {
                    Text(formatCurrency(derivationState.maintenanceSavings))
                }
            }
        }
    }

    Row(
        modifier = Modifier
            .width(1032.px)
            .gap(16.px)
            .padding(bottom = 12.px)
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
        ) {
            ProposalCalculationsView()
        }
    }
}